<script lang="ts" setup>
import { debouncedWatch } from '@vueuse/core'

defineProps({
  showDynamic: {
    default: true,
    type: Boolean,
  },
  showMailFrequency: {
    default: true,
    type: Boolean,
  },
  showMaxCompanies: {
    default: true,
    type: Boolean,
  },
  showName: {
    default: false,
    type: Boolean,
  },
  showQuery: {
    default: false,
    type: Boolean,
  },
  showWebhooks: {
    default: true,
    type: Boolean,
  },
})

const { t } = useI18n()
const { createToast } = useToasts()
const listsStore = useListsStore()
const userStore = useUserStore()

const list = defineModel<Partial<List & { webhooks?: Webhook[] }>>()
const maxCompanies = ref(list.value?.maxCompanies || 0)
const sendingTestEmail = ref(false)
const querySaving = ref(false)

const isDynamic = computed(() => {
  if (typeof list.value?.dynamic === 'undefined' || list.value?.dynamic === null) {
    return false
  }

  return list.value.dynamic
})

const mailFrequencies = computed(() => {
  return [
    {
      icon: getIcon('close'),
      label: t('forms.mailFrequency.disabled'),
      value: 'disabled',
    },
    {
      icon: getIcon('repeat'),
      label: t('forms.mailFrequency.daily'),
      value: 'daily',
    },
    {
      icon: getIcon('repeat'),
      label: t('forms.mailFrequency.weekly'),
      value: 'weekly',
    },
    {
      icon: getIcon('repeat'),
      label: t('forms.mailFrequency.monthly'),
      value: 'monthly',
    },
  ]
})

const mailFrequency = computed({
  get() {
    if (!userStore?.user?.id) {
      return mailFrequencies.value[0]
    }

    const userFrequency = mailFrequencies.value.find((frequency) => {
      if (!userStore?.user?.id) {
        return false
      }

      return frequency.value === list.value?.mailFrequencies?.[userStore.user.id]
    })

    return userFrequency || mailFrequencies.value[0]
  },
  set: async (value: any) => {
    if (!userStore?.user?.id) {
      return
    }

    if (!list.value?.id) {
      if (list.value) {
        list.value.mailFrequencies = { [userStore.user.id]: value.value }
      }

      return
    }

    try {
      await listsStore.updateList({
        listId: list.value.id,
        mailFrequency: value.value,
      })

      createToast({ message: 'listUpdated' })
    }
    catch {}
  },
})

const baseQuery = ref(JSON.stringify(list.value?.query || {}))

const queryChanged = computed(() => {
  if (!list.value?.id) {
    return false
  }

  return JSON.stringify(list.value.query) !== baseQuery.value
})

debouncedWatch(
  maxCompanies,
  async (newValue: number) => {
    if (!list.value?.id) {
      if (list.value) {
        list.value.maxCompanies = newValue
      }

      return
    }

    try {
      await listsStore.updateList({
        listId: list.value.id,
        maxCompanies: newValue,
      })

      createToast({ message: 'listUpdated' })
    }
    catch {}
  },
  { debounce: 500 },
)

async function onToggleListDynamic() {
  if (!list.value?.id) {
    if (list.value) {
      list.value.dynamic = !list.value.dynamic
    }

    return
  }

  try {
    await listsStore.updateList({
      dynamic: !list.value?.dynamic,
      listId: list.value.id,
    })

    createToast({ message: 'listUpdated' })
  }
  catch {}
}

async function onSendTestEmail() {
  if (!list.value?.id) {
    return
  }

  sendingTestEmail.value = true

  try {
    await listsStore.sendTestEmail({ listId: list.value.id })

    createToast({ message: t('forms.mailFrequency.sendTestEmailSuccess') })
  }
  catch {
    // This error will be handled by Axios interceptor (too many requests).
  }

  sendingTestEmail.value = false
}

async function onUpdateQuery() {
  if (!list.value?.id || !list.value?.query) {
    return
  }

  querySaving.value = true

  try {
    await listsStore.updateList({
      listId: list.value.id,
      query: list.value.query as SegmentationCondition[],
    })

    baseQuery.value = JSON.stringify(list.value?.query || {})

    createToast({ message: 'listUpdated' })
  }
  catch {}

  querySaving.value = false
}
</script>

<template>
  <div class="flex flex-col gap-4">
    <template v-if="showName">
      <UCard class="gradient-texture">
        <UFormGroup
          :description="t('forms.name.tooltip')"
          :label="t('forms.name.label')"
          size="lg"
        >
          <UInput
            v-model="list!.name"
            size="lg"
          />
        </UFormGroup>
      </UCard>
    </template>

    <template v-if="showDynamic">
      <UCard class="gradient-texture">
        <UFormGroup
          :description="t('forms.dynamic.tooltip')"
          :help="t('forms.dynamic.quote')"
          :label="t('forms.dynamic.label')"
          size="lg"
        >
          <UToggle
            class="mt-2 flex-initial flex-shrink-0"
            :model-value="isDynamic"
            :off-icon="getIcon('signals')"
            :on-icon="getIcon('signals')"
            size="lg"
            variant="subtle"
            @click="onToggleListDynamic"
          />
        </UFormGroup>
      </UCard>
    </template>

    <template v-if="(showQuery || showMaxCompanies || showWebhooks || showMailFrequency) && !isDynamic">
      <UAlert
        color="indigo"
        :description="t('forms.notDynamic.quote')"
        :icon="getIcon('check')"
        :title="t('forms.notDynamic.label')"
        variant="subtle"
      />
    </template>

    <template v-if="showQuery">
      <UCard
        class="gradient-texture"
        :class="{ 'pointer-events-none cursor-not-allowed select-none opacity-50': !list?.dynamic }"
      >
        <UFormGroup
          :description="t('forms.query.tooltip')"
          :label="t('forms.query.label')"
          size="lg"
        >
          <SegmentationVertical
            v-model="list!.query as SegmentationCondition[]"
            class="mt-4"
            feature="list"
            :has-save="false"
          />

          <UButton
            v-if="queryChanged"
            block
            class="mt-4"
            color="green"
            :icon="getIcon('save')"
            :label="t('forms.query.save')"
            :loading="querySaving"
            size="xs"
            variant="subtle"
            @click="onUpdateQuery"
          />
        </UFormGroup>
      </UCard>
    </template>

    <template v-if="showMaxCompanies">
      <UCard
        class="gradient-texture"
        :class="{ 'pointer-events-none cursor-not-allowed select-none opacity-50': !list?.dynamic }"
      >
        <UFormGroup
          :description="t('forms.maxCompanies.tooltip')"
          :label="t('forms.maxCompanies.label')"
          size="lg"
        >
          <div class="relative">
            <UInput
              v-model="maxCompanies"
              class="mt-4"
              :icon="getIcon('companies')"
              min="0"
              :placeholder="t('forms.maxCompanies.placeholder')"
              size="lg"
              type="number"
            />

            <UBadge
              v-if="maxCompanies === 0"
              class="absolute right-10 top-1/2 -translate-y-1/2"
              color="green"
              variant="subtle"
            >
              Unlimited
            </UBadge>
          </div>
        </UFormGroup>
      </UCard>
    </template>

    <template v-if="list?.id && showWebhooks">
      <UCard
        class="gradient-texture"
        :class="{ 'pointer-events-none cursor-not-allowed select-none opacity-50': !list?.dynamic }"
      >
        <UFormGroup
          v-if="list?.id"
          :description="t('forms.webhooks.attachWebhooksDescription')"
          :help="t('forms.webhooks.quote')"
          :label="t('forms.webhooks.attachWebhooksLabel')"
          size="lg"
        >
          <ListAttachWebhookSelector
            v-if="list"
            class="my-4 w-full"
            :list="list"
          />
        </UFormGroup>
      </UCard>
    </template>

    <template v-if="showMailFrequency && list?.id">
      <UCard
        class="gradient-texture"
        :class="{ 'pointer-events-none cursor-not-allowed select-none opacity-50': !list?.dynamic }"
      >
        <UFormGroup
          :description="t('forms.mailFrequency.tooltip')"
          :label="t('forms.mailFrequency.label')"
          name="mailFrequency"
        >
          <USelectMenu
            v-model="mailFrequency"
            :options="mailFrequencies"
            size="md"
          />

          <div class="flex w-full items-center justify-end">
            <UButton
              v-if="mailFrequency.value !== 'disabled'"
              class="mt-2"
              :icon="getIcon('plane')"
              :label="t('forms.mailFrequency.sendTestEmail')"
              size="xs"
              type="submit"
              :ui="{ icon: { base: 'text-yellow-400' } }"
              @click="onSendTestEmail"
            />
          </div>
        </UFormGroup>
      </UCard>
    </template>
  </div>
</template>

<i18n lang="json">
{
  "en": {
    "forms": {
      "dynamic": {
        "label": "Signals",
        "quote": "Enabling signals will continuously push new companies matching your segmentation to this list until its maximum capacity.",
        "tooltip": "Toggle signals feature for this list."
      },
      "mailFrequency": {
        "daily": "Daily",
        "disabled": "Disabled",
        "label": "Email notifications",
        "monthly": "Monthly",
        "sendTestEmail": "Send a test email",
        "sendTestEmailSuccess": "A test email has been sent.",
        "tooltip": "The frequency of the emails sent by this list.",
        "weekly": "Weekly"
      },
      "maxCompanies": {
        "label": "Max companies",
        "placeholder": "0 (unlimited)",
        "tooltip": "The maximum number of companies to be added in this list. (0 = unlimited)"
      },
      "name": {
        "label": "Name",
        "tooltip": "The name of the list."
      },
      "notDynamic": {
        "label": "You must enable the Signals feature to configure the rest of the options",
        "quote": "All the following settings are available to tweak how Signals will behave with this list."
      },
      "query": {
        "label": "Segmentation",
        "save": "Save",
        "tooltip": "The segmentation query of the list, used to filter companies that will be pushed to the list."
      },
      "webhooks": {
        "attachWebhooksDescription": "Attach webhooks to this list.",
        "attachWebhooksLabel": "Webhooks",
        "quote": "Attaching webhooks to a list will allow you to receive notifications or trigger workflows when new companies get matched for this list."
      }
    }
  },
  "es": {
    "forms": {
      "dynamic": {
        "label": "Señales",
        "quote": "Habilitar las señales añadirá continuamente nuevas empresas que coincidan con tu segmentación a esta lista hasta alcanzar su capacidad máxima.",
        "tooltip": "Activar o desactivar la función de señales para esta lista."
      },
      "mailFrequency": {
        "daily": "Diario",
        "disabled": "Desactivado",
        "label": "Notificaciones por correo electrónico",
        "monthly": "Mensual",
        "sendTestEmail": "Enviar un correo electrónico de prueba",
        "sendTestEmailSuccess": "Se ha enviado un correo electrónico de prueba.",
        "tooltip": "La frecuencia de los correos electrónicos enviados por esta lista.",
        "weekly": "Semanal"
      },
      "maxCompanies": {
        "label": "Máximo de empresas",
        "placeholder": "0 (ilimitado)",
        "tooltip": "El número máximo de empresas que se añadirán a esta lista. (0 = ilimitado)"
      },
      "name": {
        "label": "Nombre",
        "tooltip": "El nombre de la lista."
      },
      "notDynamic": {
        "label": "Debes activar la función de señales para configurar el resto de opciones",
        "quote": "Todas las siguientes opciones están disponibles para ajustar cómo se comportarán las señales con esta lista."
      },
      "query": {
        "label": "Segmentación",
        "save": "Guardar",
        "tooltip": "La segmentación de la lista, usada para filtrar empresas que se añadirán a la lista."
      },
      "webhooks": {
        "attachWebhooksDescription": "Adjuntar webhooks a esta lista.",
        "attachWebhooksLabel": "Webhooks",
        "quote": "Adjuntar webhooks a una lista te permitirá recibir notificaciones o activar flujos de trabajo cuando nuevas empresas coincidan con esta lista."
      }
    }
  },
  "fr": {
    "forms": {
      "dynamic": {
        "label": "Signaux",
        "quote": "L'activation des signaux va continuellement pousser de nouvelles entreprises correspondant à votre segmentation dans cette liste jusqu'à atteindre sa capacité maximale.",
        "tooltip": "Activer ou désactiver les signaux pour cette liste."
      },
      "mailFrequency": {
        "daily": "Quotidien",
        "disabled": "Désactivé",
        "label": "Notifications par email",
        "monthly": "Mensuel",
        "sendTestEmail": "Envoyer un email de test",
        "sendTestEmailSuccess": "Un email de test a été envoyé.",
        "tooltip": "La fréquence des emails envoyés par cette liste.",
        "weekly": "Hebdomadaire"
      },
      "maxCompanies": {
        "label": "Nombre maximal de sociétés",
        "placeholder": "0 (illimité)",
        "tooltip": "Le nombre maximal de sociétés à ajouter à cette liste. (0 = illimité)"
      },
      "name": {
        "label": "Nom",
        "tooltip": "Le nom de la liste."
      },
      "notDynamic": {
        "label": "Vous devez activer la fonctionnalité Signaux pour configurer les autres options",
        "quote": "Toutes les options suivantes sont disponibles pour ajuster le comportement des signaux avec cette liste."
      },
      "query": {
        "label": "Segmentation",
        "save": "Enregistrer",
        "tooltip": "La segmentation de la liste, utilisée pour filtrer les entreprises qui seront ajoutées à la liste."
      },
      "webhooks": {
        "attachWebhooksDescription": "Attacher des webhooks à cette liste.",
        "attachWebhooksLabel": "Webhooks",
        "quote": "Attacher des webhooks à une liste vous permettra de recevoir des notifications ou d'exécuter des workflows lorsqu'une nouvelle entreprise est ajoutée à cette liste."
      }
    }
  }
}
</i18n>
